import { useState, type CSSProperties, type HTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import './Avatar.css';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Image URL. If missing or fails to load, initials fall back. */
  src?: string;
  /** Used for initials fallback + image alt. */
  name?: string;
  /** Diameter in px. Default 40. */
  size?: number;
  /** 0..1 — fills a conic progress ring around the avatar in XP gold. */
  level?: number;
  /** Optional level number badge at the bottom-right (e.g. "7"). */
  levelBadge?: number | string;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Avatar — profile image with initials fallback + optional level ring.
 * Used on the home top bar and profile page.
 */
export function Avatar({
  src,
  name,
  size = 40,
  level,
  levelBadge,
  className,
  style,
  ref,
  ...rest
}: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(src) && !errored;
  const initials = computeInitials(name);

  const innerStyle: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.42),
  };

  const avatar = (
    <span
      ref={ref}
      className={clsx('fluent-avatar', className)}
      style={{ ...innerStyle, ...style }}
      aria-label={name}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt={name ?? ''}
          className="fluent-avatar__img"
          onError={() => setErrored(true)}
          draggable={false}
        />
      ) : (
        <span className="fluent-avatar__initials" aria-hidden={name ? undefined : 'true'}>
          {initials}
        </span>
      )}
      {levelBadge != null && <span className="fluent-avatar__level" aria-hidden="true">{levelBadge}</span>}
    </span>
  );

  if (level != null) {
    const progress = Math.max(0, Math.min(1, level));
    return (
      <span
        className="fluent-avatar-wrap"
        data-ring="true"
        style={{ ['--fluent-avatar-progress' as string]: String(progress) }}
      >
        {avatar}
      </span>
    );
  }

  return avatar;
}

function computeInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => Array.from(p)[0] ?? '')
    .join('')
    .toUpperCase();
}
